import { BoxProps, HStack, Text } from '@chakra-ui/react'
import { ScalarContentNode } from '@helo/cms'
import { merge } from 'lodash'

import { InlineSkeleton } from '../../components/SkeletonLoader'
import { ConditionalTooltip } from '../../components/Tooltip/Tooltip'
import { getMeaningByGoalDirection } from '../goalDirection'
import { numberFormatter } from '../numberFormatter'
import { getSuffixSizeStyles } from '../utils'

export const NumberRenderer = ({
  contentNode,
  isLoading,
  ...props
}: { isLoading?: boolean; contentNode: ScalarContentNode } & BoxProps) => {
  const showSkeleton = !!isLoading
  const mergedOptions = merge({}, contentNode.state)
  const {
    goalCondition,
    numberDisplayFormat,
    numberLocale,
    numberNullDisplay,
    numberPrecision,
    numberRoundingMode,
    numberUnit,
    size,
    textSuffix,
    styles,
    _tooltip,
  } = mergedOptions as any

  const valueMeaning = getMeaningByGoalDirection(
    goalCondition,
    contentNode.data,
  )

  const suffixSizeStyles = getSuffixSizeStyles(size)

  // Extract styles object if it exists and is an object
  const customStyles =
    styles && typeof styles === 'object'
      ? { ...styles }
      : ({} as Record<string, unknown>)

  const hasExplicitColor =
    customStyles.color != null && customStyles.color !== ''

  // Text theme baseStyle sets _dark.color, which beats a plain `color` prop in dark mode.
  // Mirror explicit colors into _dark so CMS `state.styles` wins.
  const valueStyles = hasExplicitColor
    ? {
        ...customStyles,
        _dark: {
          ...(typeof customStyles._dark === 'object' &&
          customStyles._dark !== null
            ? customStyles._dark
            : {}),
          color: customStyles.color,
        },
      }
    : customStyles

  const light = {
    color:
      valueMeaning === 'good'
        ? 'goalColors.goodTurquoise'
        : 'goalColors.badPink',
  }

  const _dark = {
    color:
      valueMeaning === 'good'
        ? 'goalColors._dark.goodTurquoise'
        : 'goalColors._dark.badRed',
  }

  const useGoalColors = Boolean(valueMeaning) && !hasExplicitColor

  // Text `size` (e.g. medium) sets lineHeight 16px; CMS `styles.fontSize` alone
  // does not clear it, so large numbers look vertically clipped / cramped.
  const valueStylesWithLineHeight =
    valueStyles.fontSize != null && valueStyles.lineHeight == null
      ? { ...valueStyles, lineHeight: 'normal' }
      : valueStyles

  return (
    <ConditionalTooltip isDisabled={showSkeleton} tooltipOptions={_tooltip}>
      <HStack alignItems="baseline" gap="6px" flexWrap="wrap" {...props}>
        <Text
          size={size}
          {...(useGoalColors ? light : {})}
          _dark={useGoalColors ? _dark : undefined}
          {...valueStylesWithLineHeight}
        >
          <InlineSkeleton isLoading={showSkeleton}>
            {numberFormatter(contentNode.data, {
              numberDisplayFormat,
              numberLocale,
              numberNullDisplay,
              numberPrecision,
              numberRoundingMode,
              numberUnit,
            })}
          </InlineSkeleton>
        </Text>
        {!showSkeleton && (
          <Text {...suffixSizeStyles} _dark={valueMeaning ? _dark : {}}>
            {textSuffix}
          </Text>
        )}
      </HStack>
    </ConditionalTooltip>
  )
}
